import { TrackingEvent } from 'src/hooks/useTrackCustomEvent';
import { EventCategory } from 'src/lib/analytics';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { CoordinateKind } from 'src/lib/graphqlTypes/types';

export const getSelectedFilterTrackingEvent = ({
  coordinateKind,
  label,
}: {
  coordinateKind: CoordinateKind;
  label: string;
}) => {
  const eventsByCoordinateKind = new Map<
    CoordinateKind,
    TrackingEvent<EventCategory>
  >([
    [
      CoordinateKind.OBJECT_FIELD,
      {
        category: 'Insights L3 Object Fields List',
        action: 'selected_object_fields_filter',
        label,
      },
    ],
    [
      CoordinateKind.INPUT_OBJECT_FIELD,
      {
        category: 'Insights L3 Input Fields List',
        action: 'selected_input_fields_filter',
        label,
      },
    ],
    [
      CoordinateKind.ENUM_VALUE,
      {
        category: 'Insights L3 Enum Values List',
        action: 'selected_enum_values_filter',
        label,
      },
    ],
  ]);

  return (
    eventsByCoordinateKind.get(coordinateKind) ??
    assertUnreachable(coordinateKind as never)
  );
};

export const getSelectedSortTrackingEvent = ({
  coordinateKind,
  label,
}: {
  coordinateKind: CoordinateKind;
  label: string;
}) => {
  const eventsByCoordinateKind = new Map<
    CoordinateKind,
    TrackingEvent<EventCategory>
  >([
    [
      CoordinateKind.OBJECT_FIELD,
      {
        category: 'Insights L3 Object Fields List',
        action: 'selected_object_fields_sort',
        label,
      },
    ],
    [
      CoordinateKind.INPUT_OBJECT_FIELD,
      {
        category: 'Insights L3 Input Fields List',
        action: 'selected_input_fields_sort',
        label,
      },
    ],
    [
      CoordinateKind.ENUM_VALUE,
      {
        category: 'Insights L3 Enum Values List',
        action: 'selected_enum_values_sort',
        label,
      },
    ],
  ]);

  return (
    eventsByCoordinateKind.get(coordinateKind) ??
    assertUnreachable(coordinateKind as never)
  );
};

export const getSelectedSortDirectionTrackingEvent = ({
  coordinateKind,
  label,
}: {
  coordinateKind: CoordinateKind;
  label: string;
}) => {
  const eventsByCoordinateKind = new Map<
    CoordinateKind,
    TrackingEvent<EventCategory>
  >([
    [
      CoordinateKind.OBJECT_FIELD,
      {
        category: 'Insights L3 Object Fields List',
        action: 'selected_object_fields_sort_direction',
        label,
      },
    ],
    [
      CoordinateKind.INPUT_OBJECT_FIELD,
      {
        category: 'Insights L3 Input Fields List',
        action: 'selected_input_fields_sort_direction',
        label,
      },
    ],
    [
      CoordinateKind.ENUM_VALUE,
      {
        category: 'Insights L3 Enum Values List',
        action: 'selected_object_enum_values_sort_direction',
        label,
      },
    ],
  ]);

  return (
    eventsByCoordinateKind.get(coordinateKind) ??
    assertUnreachable(coordinateKind as never)
  );
};

export const getExecutedSearchTrackingEvent = ({
  coordinateKind,
}: {
  coordinateKind: CoordinateKind;
}) => {
  const eventsByCoordinateKind = new Map<
    CoordinateKind,
    TrackingEvent<EventCategory>
  >([
    [
      CoordinateKind.OBJECT_FIELD,
      {
        category: 'Insights L3 Object Fields List',
        action: 'executed_insights_object_fields_search',
      },
    ],
    [
      CoordinateKind.INPUT_OBJECT_FIELD,
      {
        category: 'Insights L3 Input Fields List',
        action: 'executed_insights_input_fields_search',
      },
    ],
    [
      CoordinateKind.ENUM_VALUE,
      {
        category: 'Insights L3 Enum Values List',
        action: 'executed_insights_enum_values_search',
      },
    ],
  ]);

  return (
    eventsByCoordinateKind.get(coordinateKind) ??
    assertUnreachable(coordinateKind as never)
  );
};

export const getClearedSearchTrackingEvent = ({
  coordinateKind,
}: {
  coordinateKind: CoordinateKind;
}) => {
  const eventsByCoordinateKind = new Map<
    CoordinateKind,
    TrackingEvent<EventCategory>
  >([
    [
      CoordinateKind.OBJECT_FIELD,
      {
        category: 'Insights L3 Object Fields List',
        action: 'cleared_insights_object_fields_search',
      },
    ],
    [
      CoordinateKind.INPUT_OBJECT_FIELD,
      {
        category: 'Insights L3 Input Fields List',
        action: 'cleared_insights_input_fields_search',
      },
    ],
    [
      CoordinateKind.ENUM_VALUE,
      {
        category: 'Insights L3 Enum Values List',
        action: 'cleared_insights_enum_values_search',
      },
    ],
  ]);

  return (
    eventsByCoordinateKind.get(coordinateKind) ??
    assertUnreachable(coordinateKind as never)
  );
};

export const getSelectedFirstPageTrackingEvent = ({
  coordinateKind,
}: {
  coordinateKind: CoordinateKind;
}) => {
  const eventsByCoordinateKind = new Map<
    CoordinateKind,
    TrackingEvent<EventCategory>
  >([
    [
      CoordinateKind.OBJECT_FIELD,
      {
        category: 'Insights L3 Object Fields List',
        action: 'selected_first_page',
      },
    ],
    [
      CoordinateKind.INPUT_OBJECT_FIELD,
      {
        category: 'Insights L3 Input Fields List',
        action: 'selected_first_page',
      },
    ],
    [
      CoordinateKind.ENUM_VALUE,
      {
        category: 'Insights L3 Enum Values List',
        action: 'selected_first_page',
      },
    ],
  ]);

  return (
    eventsByCoordinateKind.get(coordinateKind) ??
    assertUnreachable(coordinateKind as never)
  );
};

export const getSelectedPreviousPageTrackingEvent = ({
  coordinateKind,
}: {
  coordinateKind: CoordinateKind;
}) => {
  const eventsByCoordinateKind = new Map<
    CoordinateKind,
    TrackingEvent<EventCategory>
  >([
    [
      CoordinateKind.OBJECT_FIELD,
      {
        category: 'Insights L3 Object Fields List',
        action: 'selected_prev_page',
      },
    ],
    [
      CoordinateKind.INPUT_OBJECT_FIELD,
      {
        category: 'Insights L3 Input Fields List',
        action: 'selected_prev_page',
      },
    ],
    [
      CoordinateKind.ENUM_VALUE,
      {
        category: 'Insights L3 Enum Values List',
        action: 'selected_prev_page',
      },
    ],
  ]);

  return (
    eventsByCoordinateKind.get(coordinateKind) ??
    assertUnreachable(coordinateKind as never)
  );
};

export const getSelectedNextPageTrackingEvent = ({
  coordinateKind,
}: {
  coordinateKind: CoordinateKind;
}) => {
  const eventsByCoordinateKind = new Map<
    CoordinateKind,
    TrackingEvent<EventCategory>
  >([
    [
      CoordinateKind.OBJECT_FIELD,
      {
        category: 'Insights L3 Object Fields List',
        action: 'selected_next_page',
      },
    ],
    [
      CoordinateKind.INPUT_OBJECT_FIELD,
      {
        category: 'Insights L3 Input Fields List',
        action: 'selected_next_page',
      },
    ],
    [
      CoordinateKind.ENUM_VALUE,
      {
        category: 'Insights L3 Enum Values List',
        action: 'selected_next_page',
      },
    ],
  ]);

  return (
    eventsByCoordinateKind.get(coordinateKind) ??
    assertUnreachable(coordinateKind as never)
  );
};

export const getSelectedLastPageTrackingEvent = ({
  coordinateKind,
}: {
  coordinateKind: CoordinateKind;
}) => {
  const eventsByCoordinateKind = new Map<
    CoordinateKind,
    TrackingEvent<EventCategory>
  >([
    [
      CoordinateKind.OBJECT_FIELD,
      {
        category: 'Insights L3 Object Fields List',
        action: 'selected_last_page',
      },
    ],
    [
      CoordinateKind.INPUT_OBJECT_FIELD,
      {
        category: 'Insights L3 Input Fields List',
        action: 'selected_last_page',
      },
    ],
    [
      CoordinateKind.ENUM_VALUE,
      {
        category: 'Insights L3 Enum Values List',
        action: 'selected_last_page',
      },
    ],
  ]);

  return (
    eventsByCoordinateKind.get(coordinateKind) ??
    assertUnreachable(coordinateKind as never)
  );
};
